import { useState } from "react";
import { TaskList, TaskForm, type Task, useTaskActions } from "@project-mgmt/feature-tasks";
import { Container, Page, Card, Button, Modal } from "@project-mgmt/ui";
import { useNavigate, useParams } from "react-router-dom";
import { useProject } from "@project-mgmt/feature-projects";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { OfflineMessage } from "../components/OfflineIndicator";
import { notify } from "../providers/ToastProvider";
import { routes } from "../utils/routes";
import type { ProjectRouteParams } from "../types/routes";
import { LoadingSpinner } from "../components/LoadingSpinner";

export function TasksPage() {
  const navigate = useNavigate();
  const { workspaceId, projectId } = useParams() as ProjectRouteParams;

  // Fetch workspace and project data independently
  const { data: workspaceData, isLoading: isLoadingWorkspace } = useWorkspaceDetails(workspaceId || "");
  const { data: projectData, isLoading: isLoadingProject } = useProject(projectId || "", workspaceId || "");

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Get task actions
  const { deleteTask, isDeleting } = useTaskActions(workspaceId || "", projectId || "");

  // Validate required params
  if (!workspaceId || !projectId) {
    return (
      <Container size="xl">
        <Page>
          <Card className="p-6 text-center">
            <p className="text-red-600">Invalid workspace or project ID</p>
            <Button onClick={() => navigate(routes.workspaces())} className="mt-4">
              Back to Workspaces
            </Button>
          </Card>
        </Page>
      </Container>
    );
  }

  // Show loading state
  if (isLoadingWorkspace || isLoadingProject) {
    return <LoadingSpinner />;
  }

  const project = projectData;

  // Handle task selection - navigate to task detail page
  const handleTaskSelect = (task: Task) => {
    navigate(routes.task(workspaceId, projectId, task.id));
  };

  // Handle successful task creation
  const handleTaskCreated = () => {
    setShowCreateForm(false);
  };

  // Handle task edit
  const handleTaskEdit = (task: Task) => {
    setSelectedTask(task);
    setShowEditForm(true);
  };

  // Handle successful task update
  const handleTaskUpdated = () => {
    setShowEditForm(false);
    setSelectedTask(null);
  };

  // Handle task delete
  const handleTaskDelete = (task: Task) => {
    setSelectedTask(task);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedTask) return;

    const result = await deleteTask(selectedTask.id);
    if (result.success) {
      notify.success("Task deleted successfully");
      setShowDeleteConfirm(false);
      setSelectedTask(null);
    } else {
      notify.error(result.error || "Failed to delete task");
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedTask(null);
  };

  return (
    <Container size="xl">
      <Page>
        <OfflineMessage className="mb-4 sm:mb-6" />

        {/* Header */}
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-bold sm:text-2xl">Tasks</h1>
            <p className="text-textSecondary mt-1 truncate text-sm">
              Manage tasks for {project?.name || "this project"}
            </p>
          </div>

          <Button onClick={() => setShowCreateForm(true)} className="w-full sm:w-auto">
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Task
          </Button>
        </div>

        {/* Tasks Content */}
        <Card className="p-3 sm:p-6">
          <TaskList
            workspaceId={workspaceId}
            projectId={projectId}
            onTaskSelect={handleTaskSelect}
            onCreateTask={() => setShowCreateForm(true)}
            onTaskEdit={handleTaskEdit}
            onTaskDelete={handleTaskDelete}
            showFilters={true}
            notificationService={notify}
          />
        </Card>

        {/* Create Task Modal */}
        <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} title="Create New Task">
          <TaskForm
            workspaceId={workspaceId}
            projectId={projectId}
            workspaceMembers={workspaceData?.members || []}
            onSuccess={handleTaskCreated}
            onCancel={() => setShowCreateForm(false)}
            notificationService={notify}
          />
        </Modal>

        {/* Edit Task Modal */}
        <Modal isOpen={showEditForm} onClose={() => setShowEditForm(false)} title="Edit Task">
          <TaskForm
            workspaceId={workspaceId}
            projectId={projectId}
            taskId={selectedTask?.id}
            workspaceMembers={workspaceData?.members || []}
            initialData={
              selectedTask
                ? {
                    title: selectedTask.title,
                    description: selectedTask.description || "",
                    priority: selectedTask.priority,
                    dueDate: selectedTask.dueDate || "",
                    amount: selectedTask.amount,
                    transactionType: selectedTask.transactionType || "",
                    assignedTo: selectedTask.assignedTo || "",
                  }
                : undefined
            }
            onSuccess={handleTaskUpdated}
            onCancel={() => setShowEditForm(false)}
            notificationService={notify}
          />
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={cancelDelete}
          title="Delete Task"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the task "{selectedTask?.title}"? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3 border-t border-gray-200 pt-4">
              <Button
                onClick={cancelDelete}
                variant="outline"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Deleting...
                  </div>
                ) : (
                  "Delete Task"
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </Page>
    </Container>
  );
}
